import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {withStyles, TableRow, TableCell, IconButton, Typography} from "@material-ui/core";
import EditIcon from '@material-ui/icons/Edit';
import DatePicker from "./DatePicker";
import {themeTypeToBool} from '../../../../../utils/helpers';
import clsx from "clsx";
import {compose} from "recompose";
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import ActionCreator from './../../../../../store/actions/index';
import {maxDateRange} from '../../../../../utils/staticData';
import {useInView} from "react-intersection-observer";

const useStyles = theme => ({
    rentRow: {
        height: 65,
        background: "rgba(149, 117, 205, 0.5)",
        "&:hover": {
            background: themeTypeToBool(theme.palette.type) ? `rgba(149, 117, 205, 1)` : `rgba(149, 117, 205, 1)`
        }
    },
    poolRow: {
        height: 65,
        background: `rgba(187, 222, 251, 0.5)`,
        "&:hover": {
            background: themeTypeToBool(theme.palette.type) ? `rgba(187, 222, 251, 1)` : `rgba(187, 222, 251, 0.7)`
        }
    },
    reservationRow: {
        height: 65,
        background: `rgba(225, 190, 231, 0.5)`,
        "&:hover": {
            background: themeTypeToBool(theme.palette.type) ? `rgba(225, 190, 231, 1)` : `rgba(225, 190, 231, 0.7)`
        }
    },
    cell: {
        textAlign: "center",
        height: 60,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
});

const descriptionDisplay = text => {
    if (text && text.length > 20) {
        return text.slice(0, 20) + "...";
    }

    return text;
};

const CustomCell = withStyles(theme => ({
    root: {
        textAlign: "center",
        height: 60,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    }
}))(props => <TableCell style={{fontSize: props.active ? "0.875rem" : "inherit"}} {...props}/>);

const RentingRow = props => {
    const {
        classes,
        data,
        carId,
        getRent,
        getPool,
        onDialogOpenCallback,
        getRentChanges,
        getRentRemarks,
        onDateChangeCallback
    } = props;

    const [hovered, setHover] = useState(false);

    const [observe, inView] = useInView();

    const handleClick = (data) => {

        if (data.typ === "Pool") {
            getPool(carId, data.wynajem_id);
            getRentChanges(data.wynajem_id);
            getRentRemarks(data.wynajem_id);
        } else {
            getRent(carId, data.wynajem_id);
            getRentChanges(data.wynajem_id);
            getRentRemarks(data.wynajem_id);
        }
        onDialogOpenCallback(data.wynajem_id, data.typ);
    };

    const handleDateFromChange = (value) => {
        onDateChangeCallback('data_od', value, data.wynajem_id);
        setHover(false);
    };

    const handleDateToChange = (value) => {
        onDateChangeCallback('data_do', value, data.wynajem_id);
        setHover(false);
    };

    return (
        <React.Fragment>
            <TableRow
                ref={observe}
                className={clsx({
                    [classes.rentRow]: data.typ === "Wynajem",
                    [classes.poolRow]: data.typ === "Pool",
                    [classes.reservationRow]: data.typ === "Rezerwacja"
                })}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                {inView ? <CustomCell
                    className={classes.cell}
                    style={{minWidth: 170, paddingLeft: 0, paddingRight: 0}}
                >
                    <DatePicker
                        visible={hovered}
                        date={data.data_od}
                        minDate={0}
                        maxDate={typeof data.data_do === "number" ? data.data_do : maxDateRange}
                        onChange={handleDateFromChange}
                        onDatePickerClose={() => setHover(false)}
                    />
                </CustomCell> : null}

                {inView ? <CustomCell
                    className={classes.cell}
                    style={{minWidth: 170, paddingLeft: 0, paddingRight: 0}}
                >
                    {
                        typeof data.data_do === "number" ?
                            <DatePicker
                                visible={hovered}
                                date={data.data_do}
                                minDate={data.data_od}
                                maxDate={maxDateRange}
                                onChange={handleDateToChange}
                                onDatePickerClose={() => setHover(false)}
                            />
                            :
                            <Typography variant="body1">
                                {data.data_do}
                            </Typography>
                    }
                </CustomCell> : null}

                {inView ? <CustomCell active={hovered ? 1 : undefined}>
                    {data.kierowca}
                </CustomCell> : null}

                {inView ? <CustomCell active={hovered ? 1 : undefined}>
                    {data.operator}
                </CustomCell> : null}

                {inView ? <CustomCell active={hovered ? 1 : undefined}>
                    {data.dystans_przed === -1 ? 'b.d.' : data.dystans_przed + 'km'}
                </CustomCell> : null}

                {inView ? <CustomCell active={hovered ? 1 : undefined}>
                    {data.dystans_po === -1 ? 'b.d.' : data.dystans_po + 'km'}
                </CustomCell> : null}

                {inView ? <CustomCell active={hovered ? 1 : undefined}>
                    {data.dystans === -1 ? 'b.d.' : data.dystans + 'km'}
                </CustomCell> : null}

                {inView ? <CustomCell>
                    {descriptionDisplay(data.opis)}
                </CustomCell> : null}

                {inView ? <CustomCell>
                    {hovered &&
                        <IconButton
                            style={{padding: 3, cursor: "pointer"}}
                            onClick={() => handleClick(data)}
                        >
                            <EditIcon/>
                        </IconButton>
                    }
                </CustomCell> : null}
            </TableRow>
        </React.Fragment>
    );
};

const mapDispatchToProps = dispatch => {
    return bindActionCreators(ActionCreator, dispatch);
};


RentingRow.propTypes = {
    classes: PropTypes.object,
    data: PropTypes.object,
    carId: PropTypes.number,
    getRent: PropTypes.func,
    getPool: PropTypes.func,
    onDialogOpen: PropTypes.func,
    onDialogOpenCallback: PropTypes.func,
    getRentChanges: PropTypes.func,
    getRentRemarks: PropTypes.func,
    onDateChangeCallback: PropTypes.func,
};

export default compose(
    withStyles(useStyles),
    connect(null,
        mapDispatchToProps
    )
)(RentingRow);
